"use client"

import { useEffect, useId, useState } from "react"

import { MarketingButton } from "@/components/marketing/marketing-action"
import { marketingFieldClass } from "@/components/marketing/marketing-field"
import { MarketingAlert } from "@/components/marketing/marketing-alert"
import { useInquiryForm } from "@/components/marketing/use-inquiry-form"
import { HoneypotField } from "@/components/marketing/honeypot-field"
import { SITE_CONFIG } from "@/lib/site-config"
import { normalizeEmailAddress } from "@/lib/utils"

// The way to still buy when checkout cannot start. It renders only where
// /pay/embedded would otherwise leave a buyer at a dead end (the checkout call
// answered 503 "temporarily unavailable" or failed any other way), never beside
// a working payment form. It takes no card details and creates no payment
// record: the form posts name, email and an optional note to /api/contact, the
// existing persist-first intake (lib/forms/inbound.ts stores it in
// inbound_messages first, then notifies the shared inbox), with the chosen
// plan or product written into the stored message.
//
// Every new visible string lives in FALLBACK_COPY so they can be approved at
// once. The business email and phone come from SITE_CONFIG, the single source.

export const FALLBACK_COPY = {
  heading: "Let's finish your order",
  lead: "Your payment couldn't be completed on this page. Leave your details below, and we'll help you complete your purchase.",
  nameLabel: "Name",
  emailLabel: "Email",
  noteLabel: "Anything we should know? (optional)",
  submit: "Send my details",
  sending: "Sending…",
  sent: "Thanks, we have your details and will be in touch to finish your order.",
  failed: "We couldn't send that. Please email or text us instead.",
  order: "Your order:",
  directLead: "Prefer to reach us directly?",
  emailWord: "Email",
  textWord: "text",
  or: "or",
  textOnly: "Or text us at",
} as const

/** Whether a checkout answer means "checkout cannot take this payment right
 *  now". A sign-in request (401) and deliberate refusals (409 already owns
 *  it, 410 no longer offered) are real answers about this buyer or plan, not
 *  an outage, so they never get the panel. Everything else does: 5xx, 429, a
 *  network failure (no status) and the ambiguous 400s. */
export function checkoutIsDown(httpStatus: number | undefined): boolean {
  return httpStatus !== 401 && httpStatus !== 409 && httpStatus !== 410
}

/** "+19292097870" shown as "+1 929-209-7870". Derived from the one stored
 *  number so the display can never drift from the link. */
function displayPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  return m ? `+1 ${m[1]}-${m[2]}-${m[3]}` : e164
}

/** What the buyer was trying to buy, in words the inbox can act on. */
export function describeChoice({
  offerTitle,
  plan,
  price,
  amount,
}: {
  offerTitle: string | null
  plan: string | null
  price: string | null
  amount: string | null
}): string {
  const parts: string[] = []
  if (offerTitle) parts.push(offerTitle)
  if (plan) parts.push(`plan ${plan}`)
  if (price) parts.push(`price ${price}`)
  if (amount) parts.push(`custom amount $${amount}`)
  return parts.length > 0 ? parts.join(", ") : "not specified"
}

const LINK_CLASS =
  "font-medium text-[var(--color-vermilion)] underline underline-offset-2 transition-colors hover:text-[var(--color-oxblood)]"

export function CheckoutFallback({
  offerTitle,
  plan,
  price,
  amount,
  showEmail = true,
  sent = false,
  onSent,
}: {
  offerTitle: string | null
  plan: string | null
  price: string | null
  amount: string | null
  /** False where the checkout notice above already prints the email address. */
  showEmail?: boolean
  /** Kept by the parent so the confirmation survives a remount. */
  sent?: boolean
  onSent?: () => void
}) {
  const headingId = useId()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")
  const { state, error, submit } = useInquiryForm({
    endpoint: "/api/contact",
    genericErrorCopy: FALLBACK_COPY.failed,
  })

  useEffect(() => {
    if (state === "ok") onSent?.()
  }, [state, onSent])

  const done = sent || state === "ok"
  const orderLine = offerTitle ?? (amount ? `$${amount}` : null)
  // The hook shows the server's own error text, which is not ours to approve.
  // Only its email validation text is kept; any other failure reads the
  // approved line that points to email or text.
  const shownError = error && normalizeEmailAddress(email) ? FALLBACK_COPY.failed : error

  function handleSubmit(e: React.FormEvent) {
    submit(e, email, (trimmedEmail) => {
      const page = `${window.location.pathname}${window.location.search}`
      const choice = describeChoice({ offerTitle, plan, price, amount })
      const message =
        `Checkout was unavailable, so this buyer asked us to finish the order.\n` +
        `Wants to buy: ${choice}\n` +
        `Checkout page: ${page}` +
        (note.trim() ? `\n\nTheir note: ${note.trim()}` : "")
      return { name: name.trim(), email: trimmedEmail, message }
    })
  }

  const fieldClass = marketingFieldClass({ className: "w-full px-4 py-3" })

  return (
    <section
      aria-labelledby={headingId}
      className="mx-auto flex w-full max-w-[34rem] flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 text-left md:p-7"
    >
      <div className="flex flex-col gap-2">
        <h2
          id={headingId}
          className="serious-display text-[clamp(1.3rem,2.2vw,1.6rem)] leading-tight text-[var(--color-ink)]"
        >
          {FALLBACK_COPY.heading}
        </h2>
        <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-muted)]">{FALLBACK_COPY.lead}</p>
        {orderLine ? (
          <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink)]">
            {FALLBACK_COPY.order} {orderLine}
          </p>
        ) : null}
      </div>

      {done ? (
        <MarketingAlert role="status" variant="success">
          {FALLBACK_COPY.sent}
        </MarketingAlert>
      ) : (
        <form method="post" onSubmit={handleSubmit} className="relative flex flex-col gap-3">
          <HoneypotField />
          <input
            type="text"
            name="name"
            required
            maxLength={256}
            aria-label={FALLBACK_COPY.nameLabel}
            placeholder={FALLBACK_COPY.nameLabel}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
          <input
            type="email"
            name="email"
            required
            maxLength={254}
            inputMode="email"
            aria-label={FALLBACK_COPY.emailLabel}
            placeholder={FALLBACK_COPY.emailLabel}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
          <textarea
            name="note"
            maxLength={3000}
            rows={3}
            aria-label={FALLBACK_COPY.noteLabel}
            placeholder={FALLBACK_COPY.noteLabel}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={fieldClass}
          />
          <MarketingButton type="submit" variant="accent" size="lg" disabled={state === "loading"}>
            {state === "loading" ? FALLBACK_COPY.sending : FALLBACK_COPY.submit}
          </MarketingButton>
          {shownError ? <MarketingAlert className="px-3 py-2 text-[0.85rem]">{shownError}</MarketingAlert> : null}
        </form>
      )}

      <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
        {showEmail ? (
          <>
            {FALLBACK_COPY.directLead} {FALLBACK_COPY.emailWord}{" "}
            <a href={`mailto:${SITE_CONFIG.supportEmail}`} className={LINK_CLASS}>
              {SITE_CONFIG.supportEmail}
            </a>{" "}
            {FALLBACK_COPY.or} {FALLBACK_COPY.textWord}{" "}
          </>
        ) : (
          <>{FALLBACK_COPY.textOnly} </>
        )}
        <a href={`sms:${SITE_CONFIG.phone}`} className={`${LINK_CLASS} whitespace-nowrap`}>
          {displayPhone(SITE_CONFIG.phone)}
        </a>
        .
      </p>
    </section>
  )
}
